import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useFeatureFlags } from 'hooks/useFeatureFlags';
import AccountUserServer from 'resources/server/AccountUserServer';
import type { GetUserSubscriptionQueryResponse } from 'resources/types/GetUserSubscription';

import useGetMemberships from './useGetMemberships';
import useGetUserSettingsQuery from './useGetUserSettingsQuery';
import { isCurrentDatePastExpDate } from './useGetUserSubscription';

export const userSubscriptionKey = 'get-user-subscription';

function useGetUserSubscriptionQuery(
  options: UseQueryOptions<GetUserSubscriptionQueryResponse>
): UseQueryResult<GetUserSubscriptionQueryResponse> {
  const { data: user } = useGetUserSettingsQuery();
  const { isSubscriptionRequired } = useFeatureFlags();
  const { data: memberships, isFetched } = useGetMemberships();

  return useQuery({
    queryKey: [userSubscriptionKey, user?.id],
    queryFn: async () => {
      let trial = await AccountUserServer.getTrial();

      const stripeSubscription = await AccountUserServer.getStripeSubscription(user?.id);

      const hasFormsPermission = memberships?.subscriptions
        ?.filter((s) => s.source !== 'Forms/DS3 Trial')
        .some((s) => s.permissions.includes('forms.*'));

      const validStripeSubscription =
        !!stripeSubscription &&
        new Date(stripeSubscription.validUntil) > new Date('1970-01-01') &&
        !isCurrentDatePastExpDate(stripeSubscription.validUntil as unknown as string);

      const hasFormsAccess = !isSubscriptionRequired || hasFormsPermission || validStripeSubscription;

      if (!hasFormsAccess && !trial) {
        trial = await AccountUserServer.createTrial(user?.id);
      }

      return {
        trial,
        hasFormsAccess,
        stripeSubscription,
      };
    },
    enabled: !!user?.id && !!options.enabled && isFetched && user.isInitialized,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export default useGetUserSubscriptionQuery;
